import React, { useEffect, useState } from "react"
import Link from "next/link"
import { Row, Col } from "react-bootstrap"
import { linksBuilder } from "../../../services/utils/linksBuilder"
import { slugify } from "../../../services/utils/slugify"
import { useArtworkDetailsActions } from "../../actions/useArtworkDetailsActions"
import { useArtworkPurchaseActions } from "../../actions/useArtworkPurchaseActions"
import { useContactAction } from "../../actions/useContactAction"
import { HelmetTitle } from "../../components/Layout/HelmetTitle"
import styles from "./ArtworkDetailPage.module.css"
import Button from "../../components/Layout/Button"
import Container from "../../components/Layout/Container"
import LikeCards from "../../components/LikeCards"
import Wrapper from "../../components/Layout/Wrapper"
import { ArtworkModal } from "./ArtworkModal"
import { ArtworkPurchaseModal } from "./ArtworkPurchaseModal"
import { imageResizer } from "../../../services/utils/imageResizer"
import { SeoTags } from "../../components/Layout/SeoTags"
import { getFileExtension } from "../../../services/utils/getFileExtension"
import { getArtworkSize } from "../../../services/utils/getArtworkSize"
import { addPossessive } from "../../../services/utils/addPossessive"
import { useAppContext } from "../../providers/AppProvider"
import { GalleryContactModal } from "../../components/GalleryContactModal"
import { formatPrice } from "../../../services/utils/formatPrice"

const ArtworkDetailPage = ({ artworkDetails, relatedArtworks, gallery }) => {
  const { profile } = useAppContext()

  const insertArplacerButtons = () => {
    ArtPlacer.clear()

    ArtPlacer.insert({
      gallery: artworkDetails.gallery.id,
      type: "1",
      text: "Try in Your Room",
      artwork: artworkDetails.id,
      size: ".artplacerSize",
      price: null,
      classname: `btn btn-secondary-light tt-u fw-700 fs-12px mb-2 mr-2 ${styles["view-button"]}`,
      after: ".artplacerButtons",
    })

    gallery.featured_space &&
      ArtPlacer.insert({
        gallery: artworkDetails.gallery.id,
        type: "2",
        text: "View in sample room",
        artwork: artworkDetails.id,
        space: gallery.featured_space.id,
        classname: `btn btn-secondary-light tt-u fw-700 fs-12px mb-2 mr-2 ${styles["view-button"]}`,
        after: ".artplacerButtons",
      })

    getFileExtension(artworkDetails.image) != "gif" &&
      ArtPlacer.insert({
        gallery: artworkDetails.gallery.id,
        type: "3",
        artwork: artworkDetails.id,
        classname: `btn btn-secondary-light tt-u fw-700 fs-12px mb-2 mr-2 ${styles["view-button"]}`,
        after: ".artplacerButtons",
      })
  }

  const {
    form,
    setForm,
    onSubmit,
    handleShow,
    handleClose,
    modal,
    error,
    errorMessage,
    success,
    size,
    onReCAPTCHAChange,
    recaptchaRef,
  } = useArtworkDetailsActions(artworkDetails, insertArplacerButtons)

  const {
    formPurchase,
    setFormPurchase,
    onSubmitPurchase,
    handleShowPurchase,
    handleClosePurchase,
    modalPurchase,
    errorPurchase,
    errorMessagePurchase,
    successPurchase,
    sizePurchase,
    onReCAPTCHAChangePurchase,
    recaptchaRefPurchase,
  } = useArtworkPurchaseActions(artworkDetails)

  const {
    form: formContact,
    setForm: setFormContact,
    onSubmit: onSubmitContact,
    handleShow: handleShowContact,
    handleClose: handleCloseContact,
    modal: modalContact,
    success: successContact,
    error: errorContact,
    errorMessage: errorMessageContact,
    onReCAPTCHAChange: onReCAPTCHAChangeContact,
    recaptchaRef: recaptchaRefContact,
    loading: loadingContact,
  } = useContactAction(gallery)

  const showMaterialOrMediumRow =
    artworkDetails.medium || artworkDetails.material

  const BreadCrumbs = ({ resposiveStyle = "d-flex" }) => {
    if (!profile.embedded || !gallery) return

    return (
      <div className={`text-muted fs-13px mt-3 ${resposiveStyle}`}>
        <Link
          href={`/galleries/${gallery.id}/artworks`}
          to={`/galleries/${gallery.id}/artworks`}
          className="text-decoration-underline">
          {addPossessive(gallery.name)} artworks
        </Link>
        <span className="mx-1">/</span>
        <span>{artworkDetails.title}</span>
      </div>
    )
  }
  const artworkPageURL = `https://discover.artplacer.com/galleries/${
    artworkDetails.gallery.domain
  }/artworks/${artworkDetails.id}-${slugify(artworkDetails.title)}`
  return (
    <Wrapper>
      <Container>
        <SeoTags
          title={`${artworkDetails.title} | Discover ArtPlacer`}
          description={`Check out ${artworkDetails.title} from ${artworkDetails.gallery.name}. Try it in your room
             or make an inquiry. Real size: ${size}. ${artworkDetails.description}`}
          image={imageResizer(artworkDetails.image, 340, `AUTO`)}
          url={artworkPageURL}
          canonical={artworkPageURL}
        />
        <HelmetTitle title={artworkDetails.title} />
        <Row className={`${profile.embedded ? "my-3" : "mb-5"}`}>
          <BreadCrumbs resposiveStyle="d-block d-md-none pt-0 pb-4 px-5" />
          <Col md={7}>
            <div className={styles.BigPostal}>
              <img
                src={imageResizer(artworkDetails.image, "AUTO", 800)}
                alt={artworkDetails.title}
              />
            </div>
          </Col>
          <Col
            md={5}
            className="px-5 d-flex flex-column justify-content-center">
            <BreadCrumbs resposiveStyle="d-none d-md-block py-sm-5 pt-md-0 py-md-4" />
            <h1 className="fs-32px fw-300 mb-3">{artworkDetails.title}</h1>
            <Row className="mb-0 mb-md-4">
              <Col md={4} className="mb-2 mb-md-0">
                <h5 className="color-black mb-0 fs-14px">Artist</h5>
                <span className="color-black mb-4 mb-md-0 font-primary fs-14px">
                  {artworkDetails.artistname}
                </span>
              </Col>
              <Col md={5} className="mb-2 mb-md-0">
                <h5 className="color-black mb-0 fs-14px">Gallery</h5>
                <Link
                  href="/galleries/[id]"
                  as={linksBuilder.galleries(artworkDetails.gallery.domain)}
                  className="color-black td-u mb-4 mb-md-0 font-primary fs-14px">
                  {artworkDetails.gallery.name}
                </Link>
              </Col>
            </Row>
            <Row className="mb-0 mb-md-4">
              <Col md={4} className="mb-2 mb-md-0">
                <h5 className="color-black mb-0 fs-14px">Size</h5>
                <span className="color-gray-super-light font-primary fs-14px artplacerSize">
                  {size}
                </span>
              </Col>
              {artworkDetails.date && (
                <Col md={5} className="mb-2 mb-md-0">
                  <h5 className="color-black mb-0 fs-14px">Date</h5>
                  <span className="color-gray-super-light font-primary fs-14px artplacerSize">
                    {artworkDetails.date}
                  </span>
                </Col>
              )}
            </Row>

            {showMaterialOrMediumRow && (
              <Row className="mb-0 mb-md-4">
                {artworkDetails.medium && (
                  <Col md={4} className="mb-2 mb-md-0">
                    <h5 className="color-black mb-0 fs-14px">Medium</h5>
                    <span className="color-gray-super-light font-primary fs-14px artplacerSize">
                      {artworkDetails.medium}
                    </span>
                  </Col>
                )}
                {artworkDetails.material && (
                  <Col md={5} className="mb-2 mb-md-0">
                    <h5 className="color-black mb-0 fs-14px">Material</h5>
                    <span className="color-gray-super-light font-primary fs-14px artplacerSize">
                      {artworkDetails.material}
                    </span>
                  </Col>
                )}
              </Row>
            )}

            {artworkDetails.description && (
              <div className="mb-2 mb-md-4">
                <h5 className="color-black mb-0 fs-14px">Description</h5>
                <span
                  className="color-gray-super-light font-primary fs-14px"
                  style={{ whiteSpace: "pre-wrap" }}>
                  {artworkDetails.description}
                </span>
              </div>
            )}

            {artworkDetails.price && (
              <Row className="mb-0 mb-md-4">
                <Col md={12} className="mb-2 mb-md-0">
                  <h5 className="color-black mb-0 fs-14px">Price</h5>
                  <span className="color-gray-super-light font-primary fs-14px">
                    {artworkDetails.sold ? (
                      "Sold"
                    ) : (
                      <>
                        {artworkDetails.gallery.currency.symbol}{" "}
                        {formatPrice(artworkDetails.price)}
                      </>
                    )}
                  </span>
                </Col>
              </Row>
            )}

            <Row className="m-0 mt-4 mt-md-0">
              <Col md={6} sm={12} className="mx-0 mb-2 pl-0 pr-md-1 px-0">
                <Button
                  name="INQUIRE"
                  onClick={handleShow}
                  variant="outline-dark"
                />
              </Col>
              {!profile.embedded && (
                <Col md={6} sm={12} className="mx-0 mb-2 pr-0 pl-md-1 px-0">
                  <Button name="PURCHASE" onClick={handleShowPurchase} />
                </Col>
              )}
            </Row>
            <div className="mt-5">
              <h5 className="color-black fs-14px mb-3">Try it!</h5>
            </div>
            <Row>
              <Col>
                <div className="artplacerButtons"></div>
              </Col>
            </Row>
            {artworkDetails.about_artist && (
              <div className="mt-5">
                <h5 className="color-black mb-0 fs-14px">
                  About {artworkDetails.artistname}
                </h5>
                <p className="color-gray-super-light font-primary fs-14px">
                  {artworkDetails.about_artist}
                </p>
              </div>
            )}
          </Col>
        </Row>
        <div>
          <h3 className="text-black mb-2 fs-24px">You may also like</h3>
          <Row>
            {relatedArtworks &&
              relatedArtworks.map((artwork) => (
                <Col key={`like-cards-${artwork.id}`} xs={6} md={4} lg={2}>
                  <LikeCards
                    title={artwork.title}
                    subtitle={artwork.artistname}
                    subSubTitle={getArtworkSize(artwork.width, artwork.height)}
                    image={artwork.image}
                    to={linksBuilder.artworks(artwork.id, artwork.title)}
                    href="/artworks/[id]"
                    type="aspect-ratio"
                  />
                </Col>
              ))}
          </Row>
        </div>

        <ArtworkModal
          form={form}
          setForm={setForm}
          artworkDetails={artworkDetails}
          modal={modal}
          onSubmit={onSubmit}
          handleClose={handleClose}
          error={error}
          errorMessage={errorMessage}
          success={success}
          recaptchaRef={recaptchaRef}
          onReCAPTCHAChange={onReCAPTCHAChange}
        />

        <ArtworkPurchaseModal
          form={formPurchase}
          setForm={setFormPurchase}
          artworkDetails={artworkDetails}
          modal={modalPurchase}
          onSubmit={onSubmitPurchase}
          handleClose={handleClosePurchase}
          error={errorPurchase}
          errorMessage={errorMessagePurchase}
          success={successPurchase}
          recaptchaRef={recaptchaRefPurchase}
          onReCAPTCHAChange={onReCAPTCHAChangePurchase}
        />

        <GalleryContactModal
          form={formContact}
          setForm={setFormContact}
          galleryDetails={gallery}
          modal={modalContact}
          onSubmit={onSubmitContact}
          handleClose={handleCloseContact}
          error={errorContact}
          errorMessage={errorMessageContact}
          success={successContact}
          recaptchaRef={recaptchaRefContact}
          onReCAPTCHAChange={onReCAPTCHAChangeContact}
        />
      </Container>
    </Wrapper>
  )
}

export default ArtworkDetailPage
